import { useItemSet } from '@exam-management/common/item-set-provider';
import { useQContent } from '@exam-management/common/qcontent-provider';
import { FormLeavingListener } from '@exam-management/components';
import { QuestionGroupType, QuestionPType, type QuestionInputData } from '@exam-management/graphql';
import { useQBankStructure } from '@exam-management/qbs-provider';
import { parseContentForApi, useParseContentForQuestion } from '@exam-management/rich-text-editor';
import { useParams } from '@exam-management/router';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, Loading } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import type { PropsWithChildren } from '@shared/utils';
import { useQueryState } from 'nuqs';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useItemSetSubQuestionEditFormLogic } from '../../hooks';

import { ItemSetSubQuestionEditFormSchema } from './constants';
import type { ItemSetSubQuestionEditFormInput } from './types';
import { getItemSetSubQuestionEditFormDefaultValues } from './utils';

type ItemSetSubQuestionEditFormProps = PropsWithChildren;

export default function ItemSetSubQuestionEditForm(
  props: Readonly<ItemSetSubQuestionEditFormProps>,
) {
  const { children } = props;

  const { qBankStructureLevels, qBankStructureLevelItems } = useQBankStructure();
  const { qContent: question } = useQContent();
  const [isLoading, setIsLoading] = useState(true);

  const { parseContentForQuestion } = useParseContentForQuestion({
    questionId: question.id,
  });
  const formContext = useForm<ItemSetSubQuestionEditFormInput>({
    resolver: zodResolver(ItemSetSubQuestionEditFormSchema),
  });

  const handleResetForm = useCallback(async () => {
    const defaultValues = await getItemSetSubQuestionEditFormDefaultValues(
      question,
      qBankStructureLevels,
      qBankStructureLevelItems,
      parseContentForQuestion,
    );

    formContext.reset(defaultValues as Partial<ItemSetSubQuestionEditFormInput>);
    setIsLoading(false);
  }, [question]);

  useEffect(() => {
    handleResetForm();
  }, [question]);

  const { showSnackbar } = useSnackbar();
  const { itemSet } = useItemSet();
  const { subQuestionId } = useParams(
    '/qbank-structures/:structureId/qcontents/item-set/:questionId/:subQuestionId',
  );
  const [, setValidationMode] = useQueryState('validate');

  const { saveSubQuestion } = useItemSetSubQuestionEditFormLogic({ itemSet });

  const convertToPType = (
    pType: ItemSetSubQuestionEditFormInput['information']['pValue']['selected'],
  ) => {
    switch (pType) {
      case 'pInitial':
        return QuestionPType.PInitial;
      case 'pStat':
        return QuestionPType.PStat;
      default:
        return null;
    }
  };

  const getPValue = (
    data: ItemSetSubQuestionEditFormInput['information']['pValue'],
  ): number | undefined => {
    switch (data.selected) {
      case 'pInitial':
        return data.pInitial === '' ? undefined : data.pInitial;
      case 'pStat':
        return data.pStat === '' ? undefined : data.pStat;
      default:
        return undefined;
    }
  };

  const saveSubQuestionAsDraft = async (data: ItemSetSubQuestionEditFormInput) => {
    const qBankStructureLevelItemId = data.location.SUB_TOPIC?.value as string;
    if (!qBankStructureLevelItemId) return null;

    const isCalculated =
      data.information.calculated === '' ? null : data.information.calculated === 'true';

    //TODO: handle pStat when available
    // const pValue =
    //   data.information.pValue.selected === 'pInitial'
    //     ? data.information.pValue.pInitial
    //     : data.information.pValue.pStat;

    const cognitiveDomains: string[] = data.properties.cognitiveDomain.map(
      (domain: { label: string; value: string }) => domain.value,
    );
    const convertedData = {
      details: parseContentForApi(data.detail.details),
      choices: data.detail.choices.map((choice) => ({
        ...choice,
        label: parseContentForApi(choice.label),
      })),
      correctAnswer: data.detail.correctAnswer,
      calculated: isCalculated,
      pType: convertToPType(data.information.pValue.selected),
      pValue: getPValue(data.information.pValue),
      pStats: data.information.pStats,
      cognitiveDomains,
      bIndexValue: data.information.bIndex,
      learningObjectiveStatement: data.properties.learningObjectiveStatement?.value ?? undefined,
      solution: parseContentForApi(data.properties.solution),
      reference: parseContentForApi(data.properties.reference),
      group: {
        id: itemSet?.id,
        name: itemSet?.name,
        type: QuestionGroupType.ItemSet,
      },
      ...data.owners,
    } as QuestionInputData;

    await saveSubQuestion(
      {
        id: subQuestionId,
        qBankStructureLevelItemId,
        data: convertedData,
      },
      {
        onCompleted: () => {
          showSnackbar({ message: 'Question has been saved', variant: 'success' });
        },
        onError: () => {
          showSnackbar({ message: 'Unable to save question.', variant: 'error' });
        },
      },
    );
  };

  const handleShowRequiredFieldsError = () => {
    showSnackbar({
      message: 'Please complete all required fields',
      variant: 'error',
    });
  };

  const handleSubmit = async (data: ItemSetSubQuestionEditFormInput) => {
    await saveSubQuestionAsDraft(data);
    // NOTE: setting validationMode to null to remove query param from url
    setValidationMode(null);
  };

  useEffect(() => {
    if (formContext.formState.isSubmitSuccessful) {
      formContext.reset(formContext.getValues());
    }
  }, [formContext.formState.isSubmitSuccessful]);
  if (isLoading) {
    return (
      <Loading
        sx={{
          height: '100dvh',
        }}
      />
    );
  }
  return (
    <FormProvider {...formContext}>
      <Form onSubmit={formContext.handleSubmit(handleSubmit, handleShowRequiredFieldsError)}>
        {children}
      </Form>
      <FormLeavingListener onSubmit={handleSubmit} />
    </FormProvider>
  );
}
